import { Controller } from "@hotwired/stimulus"

// LinkedIn Import Controller - Handles Reclaim SDK integration for LinkedIn profile import
// Reclaim SDK is pre-bundled via app/javascript/vendor/reclaim_sdk_bundle.js (esbuild) and imported in packs/application.js.
let reclaimSDKLoadPromise = null
export default class extends Controller {
  static targets = ["modal"]
  static values = { submitUrl: String, resumePayload: Object }

  connect() {
    console.log('LinkedIn Import Controller connected!', this.element)
    this.isImporting = false
    this.reclaimRetryCount = 0
    this.maxReclaimRetries = 2
    this.userCancelledVerification = false
    this.reclaimTriggerTime = 0
    this.currentSessionId = null

    // Check for mobile return on initialization
    this.checkForMobileReturn()
    
    // Add click listener to button for debugging
    const button = this.element.querySelector('[data-action*="nextStep"]')
    if (button) {
      console.log('Found nextStep button:', button)
      button.addEventListener('click', (e) => {
        console.log('Button clicked directly!', e)
      })
    } else {
      console.warn('nextStep button not found in modal')
    }
  }

  /**
   * Checks if user is returning from mobile Reclaim app and handles the flow
   */
  checkForMobileReturn() {
    if (typeof window === 'undefined') {
      return
    }

    const urlParams = new URLSearchParams(window.location.search)

    // Check if user returned from LinkedIn import
    if (urlParams.has('linkedin_import')) {
      const status = urlParams.get('linkedin_import')

      // Clean up URL
      const newUrl = window.location.origin + window.location.pathname
      window.history.replaceState({}, document.title, newUrl)

      if (status === 'success') {
        // Show progress modal immediately
        this.showProgressModal()

        // Try to retrieve proof data - this is mobile-specific handling
        this.handleMobileProofRetrieval(urlParams)
      } else if (status === 'error' || status === 'cancelled') {
        this.showError('LinkedIn import was cancelled or failed. Please try again.')
      }
    }
  }

  /**
   * Handles mobile proof retrieval when user returns from Reclaim app
   */
  handleMobileProofRetrieval(urlParams) {
    // Check if proof data is in URL parameters
    const proofData = urlParams.get('proof')
    const sessionIdFromUrl = urlParams.get('sessionId')

    if (proofData) {
      try {
        const proof = JSON.parse(decodeURIComponent(proofData))
        if (sessionIdFromUrl) {
          this.currentSessionId = sessionIdFromUrl
        }
        this.handleLinkedInVerificationSuccess([proof])
        return
      } catch (error) {
        console.error('Error parsing proof data:', error)
      }
    }

    // Get stored session info for status polling
    const sessionInfoStr = localStorage.getItem('reclaim_mobile_session')
    if (sessionInfoStr) {
      try {
        const sessionInfo = JSON.parse(sessionInfoStr)
        const { sessionId, statusUrl, submitUrl, resumePayload } = sessionInfo

        if (sessionId && statusUrl) {
          this.currentSessionId = sessionId
          // Restore the resume-flow submitUrl from before the mobile
          // Reclaim redirect — the page reload zeroed the in-memory
          // value, and without restoring it the import below would
          // POST to /api/linkedin_import (profile) instead of the
          // intended resume endpoint.
          if (submitUrl) this.submitUrlValue = submitUrl
          if (resumePayload) this.resumePayloadValue = resumePayload
          // Clean up stored session now that we have the data
          localStorage.removeItem('reclaim_mobile_session')
          this.pollMobileSessionStatus(statusUrl, sessionId)
          return
        }
      } catch (error) {
        // Clean up invalid session data
        localStorage.removeItem('reclaim_mobile_session')
      }
    }

    // Fallback for URL-based session ID
    if (sessionIdFromUrl) {
      this.currentSessionId = sessionIdFromUrl
    }

    // Clean up any remaining session data
    localStorage.removeItem('reclaim_mobile_session')
    this.handleLinkedInVerificationError('Mobile session data was lost. This is a technical issue that needs to be fixed.')
  }

  /**
   * Polls session status for mobile flows
   */
  async pollMobileSessionStatus(statusUrl, sessionId) {
    let pollCount = 0
    const maxPolls = 30 // 30 attempts (30 seconds with 1-second intervals)

    const poll = async () => {
      pollCount++

      try {
        const response = await fetch(statusUrl)
        const statusData = await response.json()

        // Extract session data correctly - mobile responses nest data in session object
        const session = statusData.session || statusData
        const proofs = this.normalizeProofs(session.proofs ?? statusData.proofs ?? [])
        const status = this.normalizeReclaimStatus(session.status || statusData.status)
        const statusV2 = this.normalizeReclaimStatus(session.statusV2 || statusData.statusV2)

        if (this.reclaimSessionComplete(status, statusV2) && proofs.length > 0) {
          // Start progress animation
          this.showProgressModal()
          this.handleLinkedInVerificationSuccess(proofs)
          return
        }

        if (this.reclaimSessionFailed(status, statusV2)) {
          this.handleLinkedInVerificationError('Mobile verification was cancelled or failed')
          return
        }

        // Keep polling for any non-terminal status. Reclaim uses multiple
        // intermediate states and casing variants across desktop/mobile flows.
        if (pollCount < maxPolls) {
          setTimeout(() => void poll(), 1000) // Poll every second
          return
        }

        // Timeout reached
        this.handleLinkedInVerificationError('Verification is taking longer than expected. Please try again.')
      } catch (error) {
        if (pollCount < maxPolls) {
          // Retry on error
          setTimeout(() => void poll(), 2000) // Wait longer on error
        } else {
          // Final failure
          this.handleLinkedInVerificationError('Failed to check mobile verification status')
        }
      }
    }

    // Start polling
    await poll()
  }

  normalizeReclaimStatus(value) {
    return value ? String(value).trim().toUpperCase() : ''
  }

  reclaimSessionComplete(status, statusV2) {
    return (
      ['COMPLETE', 'COMPLETED', 'SESSION_COMPLETED', 'PROOF_SUBMITTED'].includes(status) ||
      statusV2 === 'PROOF_SUBMITTED' ||
      (status === 'MOBILE_SUBMITTED' && statusV2 === 'PROOF_SUBMITTED')
    )
  }

  reclaimSessionFailed(status, statusV2) {
    return ['FAILED', 'CANCELLED', 'ERROR', 'EXPIRED', 'SESSION_EXPIRED'].includes(status) ||
      ['FAILED', 'CANCELLED', 'ERROR', 'EXPIRED', 'SESSION_EXPIRED'].includes(statusV2)
  }

  /**
   * Detects if the current device is a mobile device
   */
  isMobileDevice() {
    // Check screen width first (most reliable)
    if (typeof window !== 'undefined') {
      if (window.innerWidth <= 768) {
        return true
      }
    }

    // Also check user agent for mobile devices
    if (typeof navigator !== 'undefined') {
      const userAgent = navigator.userAgent.toLowerCase()
      return /android|iphone|ipad|ipod|blackberry|iemobile|opera mini|mobile/.test(userAgent)
    }

    return false
  }

  /**
   * Opens the LinkedIn import modal and starts the verification flow
   */
  async openLinkedInImport(event) {
    event?.preventDefault()
    event?.stopPropagation()

    console.log('openLinkedInImport called')

    try {
      // Prevent duplicate imports
      if (this.isImporting) {
        console.log('Already importing, skipping')
        return
      }

      this.isImporting = true
      this.reclaimRetryCount = 0

      // Show welcome modal first
      console.log('Showing welcome modal')
      this.showModal('welcome')
    } catch (error) {
      console.error('Error opening LinkedIn import:', error)
      this.isImporting = false
      this.showError('Failed to start LinkedIn import')
    }
  }

  /**
   * Starts the LinkedIn verification process with Reclaim SDK
   */
  async startLinkedInVerification() {
    try {
      // Reset state for new verification session
      this.userCancelledVerification = false

      // Clear any existing Reclaim session state first
      this.clearReclaimSession()

      // Detect device type early for use throughout the function
      const isMobile = this.isMobileDevice()

      // Ensure Reclaim SDK is loaded (global bundle)
      await this.loadReclaimSDK()

      // Get ReclaimProofRequest from window
      const ReclaimProofRequest = window.ReclaimProofRequest || window.reclaim?.ReclaimProofRequest

      if (!ReclaimProofRequest) {
        throw new Error('ReclaimProofRequest not found. Make sure Reclaim SDK is loaded.')
      }

      const bootstrap = await this.requestSignedSessionBootstrap()
      const reclaimClient = await ReclaimProofRequest.fromJsonString(bootstrap.reclaimRequestJson)

      this.currentSessionId = bootstrap.sessionId || reclaimClient.getSessionId()

      // Set modal options
      this.setReclaimModalOptions(reclaimClient, isMobile)

      // Set redirect URL for mobile flows
      if (isMobile) {
        try {
          const currentUrl = window.location.origin + window.location.pathname
          const redirectUrl = `${currentUrl}?linkedin_import=success`
          reclaimClient.setRedirectUrl(redirectUrl)

          // Get session ID and status URL for mobile polling
          const sessionId = this.currentSessionId || reclaimClient.getSessionId()
          const statusUrl = bootstrap.statusUrl || reclaimClient.getStatusUrl()

          // Store session info for mobile return. Includes the
          // resume-flow submitUrl (set by create_resume_controller via
          // `ctrl.submitUrlValue = ...`) so the post-redirect page can
          // restore the correct POST target. Without this, the mobile
          // reload loses the in-memory submitUrlValue, the fallback
          // posts to /api/linkedin_import (profile import), and the
          // user never gets a resume created / redirected to the editor.
          const sessionInfo = {
            timestamp: Date.now(),
            redirectUrl: redirectUrl,
            sessionId: sessionId,
            statusUrl: statusUrl,
            isMobile: true,
            submitUrl: this.submitUrlValue || null,
            resumePayload: this.hasResumePayloadValue ? this.resumePayloadValue : null,
          }

          try {
            localStorage.setItem('reclaim_mobile_session', JSON.stringify(sessionInfo))
          } catch (storageError) {
            console.error('Error storing session info in localStorage:', storageError)
          }
        } catch (mobileSetupError) {
          console.error('Error setting up mobile redirect:', mobileSetupError)
        }
      }

      // Show verification step
      this.showModal('verification')

      // Trigger the verification session
      this.reclaimTriggerTime = Date.now()
      await reclaimClient.triggerReclaimFlow()

      // Start the session
      await reclaimClient.startSession({
        onSuccess: (proofs) => {
          // Close the Reclaim QR modal immediately
          const reclaimModal = document.getElementById('reclaim-qr-modal')
          if (reclaimModal) {
            reclaimModal.style.display = 'none'
            reclaimModal.remove()
          }

          // Show progress modal immediately
          this.showProgressModal()
          this.handleLinkedInVerificationSuccess(proofs)
        },
        onError: (error) => {
          this.handleLinkedInVerificationError(error)
        },
      })
    } catch (error) {
      console.error('Error starting LinkedIn verification:', error)
      this.handleLinkedInVerificationError(error.message || 'Failed to start LinkedIn verification')
    }
  }

  async requestSignedSessionBootstrap() {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
    const response = await fetch('/api/linkedin_import/session', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken,
        'Accept': 'application/json',
      },
      credentials: 'same-origin',
      body: JSON.stringify({}),
    })

    const payload = await response.json().catch(() => ({}))

    if (!response.ok || !payload.success || !payload.data?.reclaimRequestJson) {
      throw new Error(payload.message || 'LinkedIn import is temporarily unavailable.')
    }

    return payload.data
  }

  /**
   * Ensures Reclaim SDK is available. The SDK is bundled via esbuild and loaded with the main application pack.
   */
  async loadReclaimSDK() {
    if (window.ReclaimProofRequest) {
      console.log('✅ Reclaim SDK already available (window.ReclaimProofRequest)')
      return
    }

    if (window.ReclaimSDK?.ReclaimProofRequest) {
      window.ReclaimProofRequest = window.ReclaimSDK.ReclaimProofRequest
      window.reclaim = Object.assign({}, window.reclaim, {
        ReclaimProofRequest: window.ReclaimProofRequest
      })
      console.log('✅ Reclaim SDK attached from global ReclaimSDK bundle')
      return
    }

    // Lazy-load the bundled SDK on demand to avoid adding it to every page upfront.
    // This file is an IIFE that attaches itself to globalThis (window), so we import it for side effects.
    try {
      if (!reclaimSDKLoadPromise) {
        reclaimSDKLoadPromise = import("../vendor/reclaim_sdk_bundle")
          .catch((e) => {
            // Allow future retries if the chunk fails to load once.
            reclaimSDKLoadPromise = null
            throw e
          })
      }

      await reclaimSDKLoadPromise
    } catch (e) {
      console.error('❌ Failed to load Reclaim SDK bundle', e)
      throw new Error('Reclaim SDK not loaded. Please refresh the page or try again later.')
    }

    if (window.ReclaimProofRequest) {
      console.log('✅ Reclaim SDK available after lazy-load (window.ReclaimProofRequest)')
      return
    }

    if (window.ReclaimSDK?.ReclaimProofRequest) {
      window.ReclaimProofRequest = window.ReclaimSDK.ReclaimProofRequest
      window.reclaim = Object.assign({}, window.reclaim, {
        ReclaimProofRequest: window.ReclaimProofRequest
      })
      console.log('✅ Reclaim SDK attached from global ReclaimSDK bundle (after lazy-load)')
      return
    }

    throw new Error('Reclaim SDK not loaded. Please refresh the page.')
  }

  /**
   * Sets consistent modal options for Reclaim SDK
   */
  setReclaimModalOptions(reclaimClient, isMobile) {
    reclaimClient.setModalOptions({
      title: 'Import your profile data from LinkedIn',
      description: 'Scan the QR code with your mobile device',
      darkTheme: false,
      extensionUrl: 'https://chromewebstore.google.com/detail/reclaim-extension/oafieibbbcepkmenknelhmgaoahamdeh',
      showExtensionInstallButton: false,
      modalPopupTimer: 3, // Modal popup timer in minutes
      forceDesktop: !isMobile,
      onClose: () => {
        this.userCancelledVerification = true
      },
    })
  }

  /**
   * Clears Reclaim session data
   */
  clearReclaimSession() {
    try {
      this.currentSessionId = null

      // Clear any existing Reclaim-related localStorage items (but preserve mobile session data)
      Object.keys(localStorage).forEach(key => {
        if (
          key.toLowerCase().includes('reclaim') &&
          key !== 'reclaim_mobile_session'
        ) {
          localStorage.removeItem(key)
        }
      })

      // Clear any existing Reclaim-related sessionStorage items
      Object.keys(sessionStorage).forEach(key => {
        if (key.toLowerCase().includes('reclaim')) {
          sessionStorage.removeItem(key)
        }
      })

      // Remove any existing Reclaim modal elements
      const existingModal = document.getElementById('reclaim-qr-modal')
      if (existingModal) {
        existingModal.remove()
      }

      // Remove any other Reclaim elements
      const reclaimElements = document.querySelectorAll('[id*="reclaim"]')
      reclaimElements.forEach((element) => {
        element.remove()
      })
    } catch (error) {
      // Ignore element removal errors silently
      console.error('Error clearing Reclaim session:', error)
    }
  }

  /**
   * Handles successful LinkedIn verification
   */
  async handleLinkedInVerificationSuccess(proofs) {
    try {
      const normalizedProofs = this.normalizeProofs(proofs)

      if (!normalizedProofs.length) {
        throw new Error('No proof data received from Reclaim')
      }

      // Parse LinkedIn data from first proof
      const linkedInData = this.parseLinkedInData(normalizedProofs[0])

      // Send to backend for import
      await this.importLinkedInDataToBackend(linkedInData)
    } catch (error) {
      console.error('Error handling LinkedIn verification success:', error)
      this.handleLinkedInVerificationError(error.message || 'Failed to process LinkedIn data')
    }
  }

  /**
   * Normalizes proof payload from Reclaim into a flat array of proof objects
   */
  normalizeProofs(rawProofs) {
    if (!rawProofs) return []

    let proofArray = []

    if (Array.isArray(rawProofs)) {
      proofArray = rawProofs
    } else if (Array.isArray(rawProofs.proofs)) {
      proofArray = rawProofs.proofs
    } else if (rawProofs.proof) {
      proofArray = [rawProofs.proof]
    } else {
      proofArray = [rawProofs]
    }

    return proofArray
      .map(proof => proof?.proof || proof)
      .filter(proof => proof && (proof.publicData || proof.claimData))
  }

  /**
   * Parses LinkedIn data from Reclaim proof
   */
  parseLinkedInData(proof) {
    try {
      if (!proof) {
        throw new Error('Proof payload is empty')
      }

      const publicData =
        proof.publicData ||
        proof.claimData?.publicData ||
        proof.claim?.publicData ||
        proof.data?.publicData

      if (!publicData) {
        throw new Error('publicData not present in proof')
      }

      // The actual LinkedIn data can appear under different keys depending on provider version
      const linkedinUserData =
        publicData.linkedinUserData ||
        publicData.linkedin_user_data ||
        publicData.linkedinProfile ||
        publicData.linkedin_profile ||
        publicData.userData ||
        publicData.user_data

      if (!linkedinUserData) {
        console.error('LinkedIn payload keys:', Object.keys(publicData))
      }

      if (!linkedinUserData) {
        throw new Error('No LinkedIn user data found in proof')
      }

      // Extract hero information if available
      const heroData = linkedinUserData.hero || {}
      const aboutData = linkedinUserData.about || {}

      const parsedData = {
        experience: linkedinUserData.experience ?? [],
        about: {
          aboutText: aboutData.aboutText ?? linkedinUserData.summary ?? '',
        },
        profile: {
          fullName:
            heroData.name ||
            heroData.fullName ||
            linkedinUserData.name ||
            linkedinUserData.fullName ||
            '',
          headline: heroData.headline ?? linkedinUserData.headline ?? '',
          collegeFromHeroSection:
            heroData.college ?? linkedinUserData.college ?? '',
          geoLocation:
            heroData.geoLocation ?? linkedinUserData.geoLocation ?? '',
          backgroundImageUrl:
            heroData.backgroundImageUrl ||
            linkedinUserData.backgroundImageUrl ||
            '',
          profilePictureUrl:
            heroData.profilePictureUrl ||
            linkedinUserData.profilePictureUrl ||
            '',
        },
        education: linkedinUserData.education ?? [],
        skills: linkedinUserData.skills ?? [],
        name:
          heroData.name ||
          linkedinUserData.name ||
          linkedinUserData.fullName ||
          '',
        linkedinHandle:
          linkedinUserData.linkedinUrl ?? linkedinUserData.profileUrl ?? '',
        connectionCount: linkedinUserData.connections || '0',
        followersCount: linkedinUserData.followers || '0',
      }

      return parsedData
    } catch (error) {
      throw new Error('Failed to parse LinkedIn profile data: ' + error.message)
    }
  }

  /**
   * Sends LinkedIn data to backend for import
   */
  async importLinkedInDataToBackend(data) {
    try {
      if (!this.currentSessionId) {
        throw new Error('LinkedIn import session is missing. Please try again.')
      }

      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
      const url = this.submitUrlValue || '/api/linkedin_import'
      // When the resume-flow caller (create_resume_controller#startLinkedin)
      // set resumePayloadValue, fold it into the body so
      // App::ResumesController#import_linkedin sees resume_name +
      // job_target alongside the LinkedIn proof — without this, every
      // LinkedIn-imported resume saved as "Untitled resume".
      const extras = this.hasResumePayloadValue ? (this.resumePayloadValue || {}) : {}
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
          'Accept': 'application/json',
        },
        credentials: 'same-origin',
        body: JSON.stringify({
          session_id: this.currentSessionId,
          publicData: data,
          ...extras,
        }),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`)
      }

      const result = await response.json()

      if (result.success) {
        // Resume-import flow: redirect straight to the new resume's editor.
        if (result.editUrl) {
          window.location = result.editUrl
          return
        }

        // Profile flow (unchanged): show success step
        this.showModal('success')
        this.dispatch('importComplete')

        // Reload page after a short delay to show updated profile
        setTimeout(() => {
          window.location.reload()
        }, 2000)
      } else {
        throw new Error(result.message || 'Import failed')
      }
    } catch (error) {
      console.error('Error importing LinkedIn data:', error)
      this.handleLinkedInVerificationError(error.message || 'Failed to import LinkedIn profile')
    }
  }

  /**
   * Handles LinkedIn verification errors
   */
  handleLinkedInVerificationError(error) {
    console.error('LinkedIn verification error:', error)
    this.isImporting = false
    this.closeProgressModal()
    
    const errorMessage = typeof error === 'string' ? error : error.message || 'LinkedIn verification failed'
    this.showError(errorMessage)
  }

  /**
   * Shows modal with specific step
   */
  showModal(step) {
    console.log('showModal called with step:', step)
    const modal = document.querySelector('[data-linkedin-import-modal]')
    if (modal) {
      // Hide all steps
      const allSteps = modal.querySelectorAll('.linkedin-import-step')
      allSteps.forEach(stepEl => {
        stepEl.style.display = 'none'
      })

      // Show the requested step
      const targetStep = modal.querySelector(`[data-step="${step}"]`)
      if (targetStep) {
        targetStep.style.display = 'block'
        console.log('Showing step:', step)
      } else {
        console.error('Target step not found:', step)
      }

      // Update current step
      modal.dataset.currentStep = step
      modal.classList.add('is-visible')
      document.body.classList.add('linkedin-import-modal-open')
      console.log('Modal should now be visible')
    } else {
      console.error('Modal element not found')
    }
  }

  /**
   * Shows progress modal
   */
  showProgressModal() {
    this.showModal('progress')
  }

  /**
   * Closes progress modal
   */
  closeProgressModal() {
    const modal = document.querySelector('[data-linkedin-import-modal]')
    if (modal) {
      modal.classList.remove('is-visible')
      document.body.classList.remove('linkedin-import-modal-open')
    }
  }

  /**
   * Shows error message
   */
  showError(message) {
    // Dispatch event for error handling
    this.dispatch('error', { detail: { message } })
    
    // Also show alert for now (can be replaced with better UI)
    alert(message)
  }

  /**
   * Closes the modal
   */
  closeModal(event) {
    event?.preventDefault()
    event?.stopPropagation()
    
    const modal = document.querySelector('[data-linkedin-import-modal]')
    if (modal) {
      modal.classList.remove('is-visible')
      document.body.classList.remove('linkedin-import-modal-open')
      
      // Reset to welcome step
      modal.dataset.currentStep = 'welcome'
      const allSteps = modal.querySelectorAll('.linkedin-import-step')
      allSteps.forEach(stepEl => {
        stepEl.style.display = 'none'
      })
      const welcomeStep = modal.querySelector('[data-step="welcome"]')
      if (welcomeStep) {
        welcomeStep.style.display = 'block'
      }
    }
    
    this.isImporting = false
  }

  /**
   * Stops event propagation (for modal dialog clicks)
   */
  stopPropagation(event) {
    event.stopPropagation()
  }

  /**
   * Proceeds to next step
   */
  async nextStep(event) {
    event?.preventDefault()
    event?.stopPropagation()
    
    console.log('nextStep called')
    
    const modal = document.querySelector('[data-linkedin-import-modal]')
    if (!modal) {
      console.error('LinkedIn import modal not found')
      return
    }

    const currentStep = modal.dataset.currentStep || 'welcome'
    console.log('Current step:', currentStep)

    try {
      switch (currentStep) {
        case 'welcome':
          console.log('Starting LinkedIn verification from welcome step')
          await this.startLinkedInVerification()
          break
        case 'verification':
          // Already handled by Reclaim SDK callbacks
          console.log('Already in verification step')
          break
        case 'progress':
          // Already handled
          console.log('Already in progress step')
          break
        case 'review':
          // Move to next review sub-step or complete
          console.log('In review step')
          break
        case 'success':
          console.log('Closing modal from success step')
          this.closeModal(event)
          break
      }
    } catch (error) {
      console.error('Error in nextStep:', error)
      this.showError(error.message || 'An error occurred')
    }
  }
}
